'use client';

import {useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent} from 'react';
import type {PublicCatalogModelData, PublicCatalogPageData, PublicCatalogQueryData} from '@zzy/contracts';
import {Button} from '../../components/ui/button';
import {NativeSelect} from '../../components/ui/native-select';
import {usePublicCopy} from './content/navigation';
import {apiTabs, catalogCategories, initialCatalogQuery, changeCatalogFilters, createCatalogReadSession, catalogSnapshotKey, type PublicCatalogReader, type CatalogReadState} from './content/api-platform';
import {publicCatalogReader} from './content/public-catalog-reader';
import {CatalogIcon, CatalogModelDetails, CatalogModelResults, CatalogReadStatus, type CatalogDetailView} from './ApiCatalog';
import styles from './api-platform.module.css';

export {CatalogPricePlans, CatalogModelDetails, CatalogModelResults, CatalogReadStatus} from './ApiCatalog';
type Tab = typeof apiTabs[number]['id'];

export function ConnectedApiPlatformPage() {
  return <ApiPlatformPage catalogReader={publicCatalogReader}/>;
}

export function ApiPlatformPage({catalogReader}: {catalogReader?: PublicCatalogReader}) {
  const {t} = usePublicCopy();
  const [tab, setTab] = useState<Tab>('models');
  const [query, setQuery] = useState(initialCatalogQuery);
  const [search, setSearch] = useState('');
  const [composing, setComposing] = useState(false);
  const [state, setState] = useState<CatalogReadState>({status: catalogReader ? 'loading' : 'unavailable'});
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [detail, setDetail] = useState<{model: PublicCatalogModelData; view: CatalogDetailView} | null>(null);
  const [manualCopy, setManualCopy] = useState('');
  const [notice, setNotice] = useState('');
  const session = useMemo(() => createCatalogReadSession(catalogReader), [catalogReader]);
  const snapshot = useRef<string | undefined>(undefined);
  const lastResult = useRef<{page: PublicCatalogPageData; query: PublicCatalogQueryData} | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const canRead = !!catalogReader;
  const searching = search.trim() !== (query.search ?? '');
  const busy = state.status === 'loading' || searching;
  // Keep the last list only while an update is pending, never after a failed read.
  const page = state.status === 'success' ? state.page : state.status === 'loading' ? lastResult.current?.page : undefined;
  const facets = lastResult.current?.page;
  const dialogOpen = !!detail || !!manualCopy;
  const filtersActive = !!(search || query.category || query.vendorId || query.groupId || query.sort !== 'model_asc');

  const updateFilters = useCallback((patch: Partial<Omit<PublicCatalogQueryData, 'cursor'>>) => {
    session.cancel();
    snapshot.current = undefined;
    setCursors([undefined]);
    setDetail(null);
    setManualCopy('');
    setState({status: canRead ? 'loading' : 'unavailable'});
    setQuery(current => changeCatalogFilters(current, patch));
  }, [session, canRead]);

  useEffect(() => {
    setState({status: canRead ? 'loading' : 'unavailable'});
    void session.read(query, query.cursor ? snapshot.current : undefined).then(result => {
      if (!result) return;
      if (result.status === 'success') {
        snapshot.current = catalogSnapshotKey(result.page.snapshot);
        lastResult.current = {page: result.page, query};
      }
      setState(result);
    });
    return () => session.cancel();
  }, [session, query, canRead]);

  useEffect(() => {
    if (composing || !searching) return;
    const timer = window.setTimeout(() => updateFilters({search: search.trim() || undefined}), 300);
    return () => window.clearTimeout(timer);
  }, [search, searching, composing, updateFilters]);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(''), 2600);
    return () => window.clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    const element = dialog.current;
    if (!element || !dialogOpen) return;
    element.showModal();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
      if (element.open) element.close();
    };
  }, [dialogOpen]);

  function closeDialog() {
    setDetail(null);
    setManualCopy('');
    setNotice('');
  }

  async function copy(value: string) {
    setNotice('');
    try {
      await navigator.clipboard.writeText(value);
      setNotice(t('已复制。'));
    } catch {
      setManualCopy(value);
    }
  }

  function clearFilters() {
    setSearch('');
    updateFilters({search: undefined, category: undefined, vendorId: undefined, groupId: undefined, sort: 'model_asc'});
  }

  function reload() {
    updateFilters({search: search.trim() || undefined});
  }

  function paginate(direction: 'previous' | 'next') {
    if (busy || state.status !== 'success') return;
    const nextCursors = direction === 'next' ? [...cursors, state.page.nextCursor ?? undefined] : cursors.slice(0, -1);
    if ((direction === 'next' && !state.page.nextCursor) || nextCursors.length === 0) return;
    session.cancel();
    setDetail(null);
    setManualCopy('');
    setState({status: 'loading'});
    setCursors(nextCursors);
    setQuery(current => ({...current, cursor: nextCursors.at(-1)}));
  }

  function navigateTabs(event: KeyboardEvent<HTMLElement>) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const nextTab = event.key === 'Home' ? 'models' : event.key === 'End' ? 'docs' : tab === 'models' ? 'docs' : 'models';
    setTab(nextTab);
    document.getElementById('api-tab-' + nextTab)?.focus();
  }

  return <main className={styles.page}>
    <div className={styles.container}>
      <header className={styles.hero}>
        <span className={styles.eyebrow}>{t('开发者服务')}</span>
        <h1>{t('API 开放平台')}</h1>
        <p>{t('查找模型、了解价格，为你的应用选择合适的 AI 能力。')}</p>
      </header>

      <div className={styles.navigationRow}>
        <div className={styles.tabs} role="tablist" aria-label={t('API 平台内容')} onKeyDown={navigateTabs}>
          {apiTabs.map(item => <Button key={item.id} id={'api-tab-' + item.id} variant="ghost" role="tab" aria-selected={tab === item.id} aria-controls={'api-panel-' + item.id} tabIndex={tab === item.id ? 0 : -1} onClick={() => setTab(item.id)}>{t(item.label)}</Button>)}
        </div>
        <a className={styles.accountLink} href="/account">{t('账户中心')}<span aria-hidden="true">→</span></a>
      </div>

      <section id="api-panel-models" className={styles.panel} role="tabpanel" aria-labelledby="api-tab-models" hidden={tab !== 'models'}>
        <div className={styles.filters}>
          <div className={styles.categories} role="group" aria-label={t('模型分类')}>
            {[{id: 'all', label: '全部'}, ...catalogCategories].map(category => <Button key={category.id} variant="ghost" disabled={!canRead} aria-pressed={(query.category ?? 'all') === category.id} onClick={() => updateFilters({category: category.id === 'all' ? undefined : category.id as PublicCatalogQueryData['category']})}>{t(category.label)}</Button>)}
          </div>
          <div className={styles.filterRow}>
            <label className={styles.searchField}><span>{t('搜索模型')}</span><div className={styles.searchInput}>
              <CatalogIcon name="search"/><input type="search" maxLength={200} disabled={!canRead} placeholder={t('输入模型名称或 ID')} value={search} onChange={event => setSearch(event.target.value)} onCompositionStart={() => setComposing(true)} onCompositionEnd={() => setComposing(false)} onKeyDown={event => {
                if (event.key === 'Enter' && !event.nativeEvent.isComposing && searching) {
                  event.preventDefault();
                  updateFilters({search: search.trim() || undefined});
                }
              }}/>
            </div></label>
            <label className={styles.selectField}><span>{t('厂商')}</span><NativeSelect disabled={!canRead} value={query.vendorId ?? ''} onChange={event => updateFilters({vendorId: event.target.value || undefined})}>
              <option value="">{t('全部厂商')}</option>{facets?.vendors.map(vendor => <option key={vendor.id} value={vendor.id}>{vendor.name}</option>)}
            </NativeSelect></label>
            <label className={styles.selectField}><span>{t('报价分组')}</span><NativeSelect disabled={!canRead} value={query.groupId ?? ''} onChange={event => updateFilters({groupId: event.target.value || undefined})}>
              <option value="">{t('全部分组')}</option>{facets?.groups.map(group => <option key={group.id} value={group.id}>{group.name}</option>)}
            </NativeSelect></label>
            <label className={styles.selectField}><span>{t('排序')}</span><NativeSelect disabled={!canRead} value={query.sort} onChange={event => updateFilters({sort: event.target.value as PublicCatalogQueryData['sort']})}>
              <option value="model_asc">{t('模型 ID A → Z')}</option><option value="model_desc">{t('模型 ID Z → A')}</option>
            </NativeSelect></label>
          </div>
          <div className={styles.filterFooter}><p>{query.groupId ? t('价格按所选分组展示。') : t('每张卡片标明报价分组，可在详情中查看其他分组与完整计费规则。')}</p>
            <Button variant="ghost" size="sm" disabled={!filtersActive || !canRead} onClick={clearFilters}>{t('清除筛选')}</Button>
          </div>
        </div>

        <div className={styles.results} aria-busy={busy} data-updating={busy && !!page}>
          <header className={styles.resultsHeader}><h2>{t('模型与价格')}{page && !busy && <span>{page.total}</span>}</h2>
            <div className={styles.resultsActions}><span role="status">{busy ? t('正在更新…') : state.status === 'success' ? t('目录已更新') : t('目录暂不可用')}</span>
              <Button variant="ghost" size="icon" disabled={!canRead || busy} aria-label={t('刷新模型与价格')} title={t('刷新模型与价格')} onClick={reload}><CatalogIcon name="refresh"/></Button>
            </div>
          </header>
          <div className={styles.resultBody} inert={busy && !!page}>
            {page ? <><CatalogReadStatus state={{status: 'success', page}} t={t}/><CatalogModelResults page={page} groupId={lastResult.current?.query.groupId} t={t} onCopy={value => void copy(value)} onDetail={(model, view) => setDetail({model, view})}/></> : <CatalogReadStatus state={state} t={t} onRetry={canRead ? reload : undefined}/>}
          </div>
          {page && page.status !== 'empty' && <div className={styles.pagination}>
            <label>{t('每页')}<NativeSelect aria-label={t('每页模型数')} disabled={busy} value={query.limit} onChange={event => updateFilters({limit: Number(event.target.value)})}>
              {[10, 30, 50, 100].map(limit => <option key={limit} value={limit}>{limit}</option>)}
            </NativeSelect></label>
            <span>{t('本页')} {page.items.length} {t('个模型')}</span>
            <nav aria-label={t('模型分页')}><Button variant="outline" disabled={busy || cursors.length === 1} onClick={() => paginate('previous')}>{t('上一页')}</Button><span>{t('第')} {cursors.length} {t('页')}</span><Button variant="outline" disabled={busy || !page.nextCursor} onClick={() => paginate('next')}>{t('下一页')}</Button></nav>
          </div>}
        </div>
      </section>

      <section id="api-panel-docs" className={styles.panel} role="tabpanel" aria-labelledby="api-tab-docs" hidden={tab !== 'docs'}>
        <header className={styles.docsHeading}><h2>{t('开始接入 API')}</h2><p>{t('先确认模型与计费规则，再根据对应接口文档完成接入。')}</p></header>
        <div className={styles.docsGrid}>
          <article className={styles.guide}><h3>{t('三步开始')}</h3><ol>
            <li><span>01</span><div><h4>{t('选择模型与报价分组')}</h4><p>{t('按能力、厂商和分组查找模型，核对计费单位及适用条件。')}</p></div></li>
            <li><span>02</span><div><h4>{t('准备 API 密钥')}</h4><p>{t('在所使用的 API 服务控制台创建密钥，并确认可用额度及模型权限。请妥善保管密钥。')}</p></div></li>
            <li><span>03</span><div><h4>{t('按接口文档接入')}</h4><p>{t('使用公开模型 ID，按对应文档补齐请求参数；调用后核对结果、用量和账单。')}</p></div></li>
          </ol></article>
          <article className={styles.docsCard}>
            <span className={styles.eyebrow}>{t('模型接口')}</span><h3>{t('找到模型，再查看接入信息')}</h3>
            <p>{t('在模型与价格中搜索所需模型，点击「查看详情」，切换到「接口信息」。')}</p>
            <Button onClick={() => setTab('models')}>{t('查找模型')}<span aria-hidden="true">→</span></Button>
            <div className={styles.docsNote}><h4>{t('核对接入地址与参数')}</h4><p>{t('公开目录提供模型标识、价格和已发布的接口路径。API Base URL、完整请求参数和异步结果查询方式，请以你所使用的 API 服务文档为准。')}</p></div>
          </article>
        </div>
      </section>
      <p className={styles.catalogNote}>{t('模型与价格以公开目录为准，具体费用按所用分组、计费条件和实际用量结算。')}</p>
    </div>

    {notice && !dialogOpen && <div className={styles.toast} role="status">{notice}</div>}
    <dialog ref={dialog} className={styles.dialog} aria-labelledby="api-detail-title" onCancel={event => {event.preventDefault(); closeDialog();}} onClose={closeDialog} onClick={event => {
      if (event.target !== event.currentTarget) return;
      const bounds = event.currentTarget.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeDialog();
    }}>
      <header className={styles.dialogHeader}><h2 id="api-detail-title">{manualCopy ? t('手动复制') : detail?.model.name}</h2><Button variant="ghost" size="icon" aria-label={t('关闭详情')} onClick={closeDialog}><span aria-hidden="true">×</span></Button></header>
      <div className={styles.dialogBody}>
        {manualCopy ? <><p>{t('复制未完成，请手动选择内容。')}</p><textarea className={styles.manualCopy} readOnly autoFocus aria-label={t('待复制内容')} value={manualCopy} onFocus={event => event.currentTarget.select()}/>{detail && <Button variant="outline" onClick={() => setManualCopy('')}>{t('返回模型详情')}</Button>}</> : detail && page ? <>
          <div className={styles.detailTabs} role="group" aria-label={t('模型详情')}>
            {([{id: 'prices', label: '完整价格'}, {id: 'specs', label: '模型规格'}, {id: 'docs', label: '接口信息'}] as const).map(item => <Button key={item.id} variant="ghost" aria-pressed={detail.view === item.id} onClick={() => setDetail({...detail, view: item.id})}>{t(item.label)}</Button>)}
          </div>
          <CatalogModelDetails model={detail.model} page={page} t={t} view={detail.view} onCopy={value => void copy(value)}/>
        </> : null}
        <div className={styles.dialogFeedback} role="status">{notice}</div>
      </div>
    </dialog>
  </main>;
}
